import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

/**
 * Formata uma data UTC para o timezone do usuário
 * @param date Data em UTC (string ISO ou objeto Date)
 * @param formatPattern Padrão de formatação (date-fns)
 * @param timezone Timezone do usuário (IANA timezone, ex: America/Sao_Paulo)
 * @returns String formatada com a data no timezone do usuário
 */
export function formatDateInTimezone(
  date: string | Date | null | undefined,
  formatPattern: string = 'dd/MM/yyyy HH:mm',
  timezone: string = 'UTC'
): string {
  if (!date) return '-';

  try {
    // Converter string para objeto Date se necessário
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Formatar no timezone do usuário
    return formatInTimeZone(dateObj, timezone, formatPattern, { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data com timezone:', error);
    return '-';
  }
}

/**
 * Converte uma data local (no timezone do usuário) para UTC para armazenamento
 * @param date Data local (no timezone do usuário)
 * @param timezone Timezone do usuário (IANA timezone, ex: America/Sao_Paulo)
 * @returns Data convertida para UTC
 */
export function convertToUTC(date: Date, timezone: string): Date {
  if (!date) return new Date();

  try {
    return zonedTimeToUtc(date, timezone);
  } catch (error) {
    console.error('Erro ao converter data para UTC:', error);
    return date; // Fallback para a data original
  }
}

/**
 * Formata uma data para ISO 8601 em UTC (para envio ao servidor)
 * @param date Data local (no timezone do usuário)
 * @param timezone Timezone do usuário (IANA timezone, ex: America/Sao_Paulo)
 * @returns String ISO 8601 em UTC
 */
export function formatToUTCISOString(date: Date, timezone: string): string {
  if (!date) return new Date().toISOString();

  try {
    const utcDate = zonedTimeToUtc(date, timezone);
    return utcDate.toISOString();
  } catch (error) {
    console.error('Erro ao formatar data para ISO UTC:', error);
    return date.toISOString(); // Fallback para ISO da data original
  }
}

/**
 * Obtém a lista de fusos horários (IANA names) para seleção pelo usuário
 * @returns Array de objetos com valor e nome dos timezones
 */
export function getTimezoneOptions(): { value: string, label: string }[] {
  return [
    { value: 'UTC', label: 'UTC (Tempo Universal Coordenado)' },
    { value: 'America/Sao_Paulo', label: 'Brasília (UTC-3)' },
    { value: 'America/New_York', label: 'Nova York (UTC-5/UTC-4)' },
    { value: 'America/Chicago', label: 'Chicago (UTC-6/UTC-5)' },
    { value: 'America/Denver', label: 'Denver (UTC-7/UTC-6)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/UTC-7)' },
    { value: 'Europe/London', label: 'Londres (UTC+0/UTC+1)' },
    { value: 'Europe/Paris', label: 'Paris (UTC+1/UTC+2)' },
    { value: 'Europe/Berlin', label: 'Berlim (UTC+1/UTC+2)' },
    { value: 'Europe/Moscow', label: 'Moscou (UTC+3)' },
    { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
    { value: 'Asia/Shanghai', label: 'Xangai (UTC+8)' },
    { value: 'Asia/Tokyo', label: 'Tóquio (UTC+9)' },
    { value: 'Australia/Sydney', label: 'Sydney (UTC+10/UTC+11)' },
    { value: 'Pacific/Auckland', label: 'Auckland (UTC+12/UTC+13)' },
  ];
} 